import { createSelector } from '@reduxjs/toolkit'

// === BASIC SELECTORS ===

export const selectSpacesRaw = (state) => state.wiki.spaces
export const selectPages = (state) => state.wiki.pages
export const selectArchivedSpaces = (state) => state.wiki.archivedSpaces
export const selectArchivedPages = (state) => state.wiki.archivedPages
export const selectPageContents = (state) => state.wiki.pageContents
export const selectSearchQuery = (state) => state.wiki.searchQuery
export const selectSearchResults = (state) => state.wiki.searchResults
export const selectCurrentSpaceId = (state) => state.wiki.currentSpaceId
export const selectCurrentPageId = (state) => state.wiki.currentPageId
export const selectWikiLoading = (state) => state.wiki.loading
export const selectWikiError = (state) => state.wiki.error
export const selectWikiInitialized = (state) => state.wiki.initialized

// === MAP SELECTORS FOR O(1) LOOKUPS ===

// Map selectors for O(1) lookups - used internally by other selectors
export const selectPagesMap = createSelector(
  [selectPages],
  (pages) => new Map(pages.map(p => [p.id, p]))
)

// Space map for O(1) lookups
export const selectSpacesMap = createSelector(
  [selectSpacesRaw],
  (spaces) => new Map(spaces.map(s => [s.id, s]))
)

// Pre-computed pages grouped by space for O(1) space lookups
export const selectPagesBySpaceMap = createSelector(
  [selectPages],
  (pages) => {
    const map = new Map()
    for (const page of pages) {
      if (page.is_archived) continue
      const spaceId = page.space_id
      if (!map.has(spaceId)) {
        map.set(spaceId, { pages: [], latestUpdatedAt: null })
      }
      const entry = map.get(spaceId)
      entry.pages.push(page)
      if (!entry.latestUpdatedAt || page.updated_at > entry.latestUpdatedAt) {
        entry.latestUpdatedAt = page.updated_at
      }
    }
    return map
  }
)

// === ENRICHED SELECTORS ===

// Enriched spaces selector with page count and last updated - now O(1) per space
// Defensively excludes archived spaces (active state should never hold them, but
// realtime merges could momentarily flag one).
export const selectSpaces = createSelector(
  [selectSpacesRaw, selectPagesBySpaceMap],
  (spaces, pagesBySpaceMap) => {
    return spaces
      .filter(space => !space.is_archived)
      .map(space => {
        const entry = pagesBySpaceMap.get(space.id)
        return {
          ...space,
          page_count: entry?.pages.length || 0,
          last_page_updated_at: entry?.latestUpdatedAt || null
        }
      })
  }
)

// === ARCHIVED SELECTORS ===

// Archived pages that are shown standalone in the Archived view: those whose space
// is still active AND that are the root of their archived sub-tree (parent not archived).
// Pages inside an archived space are represented by the space itself, not listed here.
export const selectArchivedRootPages = createSelector(
  [selectArchivedPages, selectArchivedSpaces],
  (archivedPages, archivedSpaces) => {
    const archivedSpaceIds = new Set(archivedSpaces.map(s => s.id))
    const archivedPageIds = new Set(archivedPages.map(p => p.id))
    return archivedPages.filter(p =>
      !archivedSpaceIds.has(p.space_id) &&
      (p.parent_page_id == null || !archivedPageIds.has(p.parent_page_id))
    )
  }
)

// Count for the "Archived" navigator badge: archived spaces + standalone archived pages
export const selectArchivedItemCount = createSelector(
  [selectArchivedSpaces, selectArchivedRootPages],
  (archivedSpaces, archivedRootPages) => archivedSpaces.length + archivedRootPages.length
)

// === DERIVED SELECTORS ===

export const selectCurrentSpace = createSelector(
  [selectSpaces, selectCurrentSpaceId],
  (spaces, currentId) => spaces.find(s => s.id === currentId) || null
)

export const selectCurrentPage = createSelector(
  [selectPages, selectCurrentPageId],
  (pages, currentId) => pages.find(p => p.id === currentId) || null
)

// Get page content from cache
export const selectPageContent = createSelector(
  [selectPageContents, (_, pageId) => pageId],
  (pageContents, pageId) => pageContents[pageId]?.content || ''
)

// Get pages for a specific space
export const selectPagesBySpace = createSelector(
  [selectPages, (_, spaceId) => spaceId],
  (pages, spaceId) => pages.filter(p => p.space_id === spaceId)
)

// Build page tree from flat array
export const selectPageTree = createSelector(
  [selectPagesBySpace],
  (pages) => {
    const buildTree = (parentId = null) => {
      return pages
        .filter(p => p.parent_page_id === parentId)
        .sort((a, b) => a.display_order - b.display_order)
        .map(page => ({
          ...page,
          children: buildTree(page.id)
        }))
    }
    return buildTree(null)
  }
)

// Get all favorite pages (global across spaces)
export const selectFavoritePages = createSelector(
  [selectPages],
  (pages) => pages
    .filter(p => p.is_favorite)
    .sort((a, b) => b.updated_at.localeCompare(a.updated_at))
)

// Get recently updated pages (top 10)
export const selectRecentlyUpdatedPages = createSelector(
  [selectPages],
  (pages) => [...pages]
    .sort((a, b) => b.updated_at.localeCompare(a.updated_at))
    .slice(0, 10)
)

// Build breadcrumb trail for a page (ancestors + current page, NOT including space)
// Space is rendered separately in WikiBreadcrumb component
// Uses pagesMap for O(1) lookups instead of O(n) find per ancestor
export const selectBreadcrumbs = createSelector(
  [selectPagesMap, (_, pageId) => pageId],
  (pagesMap, pageId) => {
    if (!pageId) return []

    const currentPage = pagesMap.get(pageId)
    if (!currentPage) return []

    const breadcrumbs = []

    // Build page trail from path array (ancestors only) - now O(1) per ancestor
    if (currentPage.path && currentPage.path.length > 0) {
      for (const ancestorId of currentPage.path) {
        const page = pagesMap.get(ancestorId)
        if (page) {
          breadcrumbs.push({ type: 'page', id: page.id, title: page.title })
        }
      }
    }

    // Add current page
    breadcrumbs.push({ type: 'page', id: currentPage.id, title: currentPage.title })

    return breadcrumbs
  }
)

// Get spaces grouped by context type
export const selectSpacesByContext = createSelector(
  [selectSpaces],
  (spaces) => {
    const standalone = spaces.filter(s => !s.context_type)
    const projectSpaces = spaces.filter(s => s.context_type === 'project')
    const goalSpaces = spaces.filter(s => s.context_type === 'annual_goal')

    return { standalone, projectSpaces, goalSpaces }
  }
)

// Get wiki spaces linked to a specific project
export const selectSpacesByProjectId = createSelector(
  [selectSpaces, (_, projectId) => projectId],
  (spaces, projectId) =>
    projectId ? spaces.filter(s => s.project_id === projectId) : []
)

// Get wiki spaces linked to a specific annual goal
export const selectSpacesByAnnualGoalId = createSelector(
  [selectSpaces, (_, annualGoalId) => annualGoalId],
  (spaces, annualGoalId) =>
    annualGoalId ? spaces.filter(s => s.annual_goal_id === annualGoalId) : []
)

// === PAGE-TO-ENTITY LINKING SELECTORS ===

// Get wiki pages linked to a specific project
export const selectPagesByProjectId = createSelector(
  [selectPages, (_, projectId) => projectId],
  (pages, projectId) =>
    projectId ? pages.filter(p => p.project_id === projectId) : []
)

// Get wiki pages linked to a specific annual goal
export const selectPagesByAnnualGoalId = createSelector(
  [selectPages, (_, annualGoalId) => annualGoalId],
  (pages, annualGoalId) =>
    annualGoalId ? pages.filter(p => p.annual_goal_id === annualGoalId) : []
)

// Get wiki pages linked to a specific task
export const selectPagesByTaskId = createSelector(
  [selectPages, (_, taskId) => taskId],
  (pages, taskId) =>
    taskId ? pages.filter(p => p.task_id === taskId) : []
)

// === SLUG-BASED SELECTORS (for human-readable URLs) ===

// Find space by slug
export const selectSpaceBySlug = createSelector(
  [selectSpaces, (_, slug) => slug],
  (spaces, slug) => slug ? spaces.find(s => s.slug === slug) || null : null
)

// Find page by slug within a space (requires spaceId)
export const selectPageBySlugInSpace = createSelector(
  [selectPages, (_, spaceId) => spaceId, (_, __, slug) => slug],
  (pages, spaceId, slug) => {
    if (!spaceId || !slug) return null
    return pages.find(p => p.space_id === spaceId && p.slug === slug) || null
  }
)

// === CONTINUE READING SELECTORS ===

// Get the most recently viewed page for "Continue Reading" feature
export const selectContinueReadingPage = createSelector(
  [selectPages, selectSpacesMap],
  (pages, spacesMap) => {
    // Filter to pages that have been viewed
    const viewedPages = pages.filter(p => p.last_viewed_at)

    if (viewedPages.length === 0) return null

    // Sort by last_viewed_at descending and get the most recent
    // Use string comparison for ISO dates (faster than Date parsing)
    const mostRecent = [...viewedPages].sort(
      (a, b) => b.last_viewed_at.localeCompare(a.last_viewed_at)
    )[0]

    // Include space info for display - O(1) lookup from map
    const space = spacesMap.get(mostRecent.space_id)

    return {
      ...mostRecent,
      space_name: space?.name,
      space_icon: space?.icon
    }
  }
)

// Get the 5 most recently read pages
export const selectRecentlyReadPages = createSelector(
  [selectPages, selectSpacesMap],
  (pages, spacesMap) => {
    const viewedPages = pages.filter(p => p.last_viewed_at)

    return [...viewedPages]
      .sort((a, b) => b.last_viewed_at.localeCompare(a.last_viewed_at))
      .slice(0, 5)
      .map(page => {
        const space = spacesMap.get(page.space_id)
        return {
          ...page,
          space_name: space?.name,
          space_icon: space?.icon_name
        }
      })
  }
)
